import type { ColumnDef } from '@tanstack/react-table';

import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { selectColumn } from '@/components/dataTable/lib/selectColumn';
import { useDynamicLocale } from '@/hoslog-intl';
import { CheckIcon, CrossIcon } from '@/icons';
import { formatAmount } from '@/lib/format';
import { MonthlyReportData } from '@/services/payment/payment.types';

const statusClasses: Record<MonthlyReportData['status'], string> = {
  PENDING: 'text-warning',
  APPROVED: 'text-success',
  REJECTED: 'text-destructive-text',
};

export function getColumns(t: ReturnType<typeof useDynamicLocale>['t']): ColumnDef<MonthlyReportData>[] {
  const statusLabels: Record<MonthlyReportData['status'], string> = {
    PENDING: t('common.page.table.pending'),
    APPROVED: t('common.page.table.approved'),
    REJECTED: t('common.page.table.rejected'),
  };

  return [
    selectColumn as ColumnDef<MonthlyReportData>,
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row }) => <div className="font-medium truncate max-w-[10rem]">{row.getValue('sn')}</div>,
      enableSorting: false,
    },
    {
      accessorKey: 'requestId',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.requestId')} />,
      cell: ({ row }) => <div className="font-medium truncate max-w-[20rem]">{row.getValue('requestId')}</div>,
      meta: {
        label: t('common.page.tableColumn.requestId'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'requestedDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.requestedDate')} />
      ),
      cell: ({ row }) => <div>{row.getValue('requestedDate')}</div>,
      meta: {
        label: t('common.page.tableColumn.requestedDate'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'bankType',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.bankType')} />,
      cell: ({ row }) => <div>{row.getValue('bankType')}</div>,
      meta: {
        label: t('common.page.tableColumn.bankType'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'bankInfo',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.bankInfo')} />,
      cell: ({ row }) => <div>{row.getValue('bankInfo')}</div>,
      meta: {
        label: t('common.page.tableColumn.bankInfo'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'accountSymbol',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.accountSymbol')} />
      ),
      cell: ({ row }) => <div>{row.getValue('accountSymbol')}</div>,
      meta: {
        label: t('common.page.tableColumn.accountSymbol'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'accountNumber',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.accountNumber')} />
      ),
      cell: ({ row }) => <div>{row.getValue('accountNumber')}</div>,
      meta: {
        label: t('common.page.tableColumn.accountNumber'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'accountHolderName',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.accountHolderName')} />
      ),
      cell: ({ row }) => <div>{row.getValue('accountHolderName')}</div>,
      meta: {
        label: t('common.page.tableColumn.accountHolderName'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'requestedAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.requestedAmount')} />
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-xs font-medium">
          {formatAmount(row.getValue<number>('requestedAmount'), '円', 'en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      ),
      meta: {
        label: t('common.page.tableColumn.requestedAmount'),
        align: 'right',
      },
      enableHiding: false,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ cell }) => {
        const status = cell.getValue<MonthlyReportData['status']>();
        return (
          <span className={`whitespace-nowrap text-xs font-medium ${statusClasses[status]}`}>
            {statusLabels[status]}
          </span>
        );
      },
      meta: {
        label: t('common.page.tableColumn.status'),
      },
      enableHiding: false,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'remarks',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.remarks')} />,
      cell: ({ row }) => <div>{row.getValue('remarks')}</div>,
      meta: {
        label: t('common.page.tableColumn.remarks'),
      },
      enableHiding: false,
    },
    {
      id: 'actions',
      accessorKey: 'Actions',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.action')} />,
      cell: ({ row }) => (
        <>
          {row.getValue('status') === 'PENDING' ? (
            <div className="flex gap-4">
              <CheckIcon className="text-success cursor-pointer" />
              <CrossIcon className="text-destructive-text cursor-pointer" />
            </div>
          ) : (
            ''
          )}
        </>
      ),
      size: 40,
      enableSorting: false,
      enableHiding: false,
    },
  ];
}
